import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Gender, GetUserPersonalInfoQuery } from '@/_gql/graphql'

import { apiClient } from '@/services/rest/http'
import {
  DURATION_STAY_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RESPONSIBILITY_OPTIONS,
} from '@/services/user/constant'
import { queryUserInformation } from '@/services/user/query'

import { Form, FormField } from '@/components/ui/form'
import { FormFieldDatePicker } from '@/components/ui/form-field/date-picker'
import { FormFieldset } from '@/components/ui/form-field/fieldset'
import { FileUploader } from '@/components/ui/form-field/file-uploader'
import { FormFieldSelect } from '@/components/ui/form-field/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import {
  formatDateOnly,
  formatDateTimeServer,
  valueDateWithFallback,
  valueWithFallback,
} from '@/lib/utils/utils'

import { CardProfile } from '../digishoku/profile/-components/card-profile'

// Constants for dropdown options - Based on backend enum values

export const Route = createFileRoute('/_user/profile/personal')({
  loader: async ({ context: { queryClient: qc } }) => {
    const [information] = await Promise.all([
      qc.fetchQuery(queryUserInformation()),
    ])

    return {
      information,
      async refetch() {
        await qc.refetchQueries(queryUserInformation())
      },
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { information, refetch } = Route.useLoaderData()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const form = useForm<FormDto>({
    resolver: zodResolver(FormDto),
    defaultValues: getDefaultValues(information),
  })
  const { mutateAsync } = useMutation({
    mutationFn: async (data: FormDto) => {
      const formData = constructPayload(
        data,
        information?.id
          ? { id: information.id, created_at: information.created_at }
          : undefined
      )
      await apiClient.post('/api/user/personal-info/simple-upsert', formData)
      await refetch()
      router.invalidate()
      navigate({ to: '/profile/education' })
      return true
    },
  })

  const handleOnSubmit = form.handleSubmit((data) => {
    toast.promise(mutateAsync(data), {
      loading: 'Menyimpan informasi pribadi',
      success: 'Informasi pribadi berhasil disimpan',
      error: 'Terjadi kesalahan saat menyimpan informasi',
    })
  }, console.error)

  return (
    <Form {...form}>
      <CardProfile title="Informasi Pribadi" isRequired={true}>
        <form onSubmit={handleOnSubmit}>
          <div className="flex flex-col gap-y-6 p-4 !pt-0 md:p-6">
            {/* Nama Lengkap */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormFieldset
                  label="Nama Lengkap"
                  className="col-span-full md:col-span-3"
                  isRequired={true}
                >
                  <Input placeholder="Masukkan nama lengkap" {...field} />
                </FormFieldset>
              )}
            />

            {/* Nama Hiragana */}
            <FormField
              control={form.control}
              name="name_hiragana"
              render={({ field }) => (
                <FormFieldset
                  label="Nama (Hiragana)"
                  className="col-span-full md:col-span-3"
                >
                  <Input placeholder="例: ルクマヌル・ハキム" {...field} />
                </FormFieldset>
              )}
            />
            <span className="text-sm text-gray-500">
              * Nama (Hiragana) Wajib diisi untuk lowongan kerja Jepang
            </span>

            {/* Foto Profil */}
            <FormField
              control={form.control}
              name="photo_profile"
              render={({ field }) => (
                <FormFieldset label="Foto Profil" className="col-span-full">
                  <FileUploader
                    value={field.value ? [field.value] : []}
                    onValueChange={(files) => {
                      field.onChange(files?.[0] || null)
                    }}
                    fileRuleInfo="PNG, JPG up to 5MB"
                    maxSize={1024 * 1024 * 5}
                  />
                </FormFieldset>
              )}
            />

            {/* Grid untuk field yang berdampingan */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Jenis Kelamin */}
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormFieldset
                    label="Jenis Kelamin"
                    className="col-span-full"
                    isRequired={true}
                  >
                    <FormFieldSelect
                      placeholder="Pilih jenis kelamin"
                      items={GENDER_OPTIONS}
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  </FormFieldset>
                )}
              />

              {/* Usia */}
              <FormField
                control={form.control}
                name="age"
                render={({ field }) => (
                  <FormFieldset
                    label="Usia"
                    className="col-span-full"
                    isRequired={true}
                  >
                    <Input
                      type="number"
                      placeholder="Masukkan usia"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || '')
                      }
                    />
                  </FormFieldset>
                )}
              />
            </div>

            {/* Tanggal Lahir */}
            <FormField
              control={form.control}
              name="birthdate"
              render={({ field }) => (
                <FormFieldset
                  label="Tanggal lahir"
                  className="col-span-full md:col-span-2"
                  noFormControl
                  isRequired={true}
                >
                  <FormFieldDatePicker
                    date={field.value}
                    onSelect={field.onChange}
                  />
                </FormFieldset>
              )}
            />

            {/* Alamat */}
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormFieldset
                  label="Alamat Lengkap, Kode POS"
                  className="col-span-full md:col-span-3"
                  isRequired={true}
                >
                  <Input placeholder="Masukkan alamat lengkap" {...field} />
                </FormFieldset>
              )}
            />

            {/* Status Pernikahan */}
            <FormField
              control={form.control}
              name="marital_status"
              render={({ field }) => (
                <FormFieldset
                  label="Status Pernikahan"
                  className="col-span-full md:col-span-2"
                >
                  <FormFieldSelect
                    placeholder="Pilih status pernikahan"
                    items={MARITAL_STATUS_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                </FormFieldset>
              )}
            />

            {/* Grid untuk field tanggungan */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Tanggung Jawab terhadap Pasangan */}
              <FormField
                control={form.control}
                name="spouse_responsibility"
                render={({ field }) => (
                  <FormFieldset
                    label="Tanggung Jawab terhadap Pasangan"
                    className="col-span-full"
                  >
                    <FormFieldSelect
                      placeholder="Pilih opsi"
                      items={RESPONSIBILITY_OPTIONS}
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  </FormFieldset>
                )}
              />

              {/* Jumlah Tanggungan (spouse_count) */}
              <FormField
                control={form.control}
                name="spouse_count"
                render={({ field }) => (
                  <FormFieldset
                    label="Jumlah Tanggungan (Tidak termasuk pasangan)"
                    className="col-span-full"
                  >
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormFieldset>
                )}
              />
            </div>

            {/* Jiko PR */}
            <FormField
              control={form.control}
              name="jiko_pr"
              render={({ field }) => (
                <FormFieldset
                  label="Jiko PR (自己PR)"
                  className="col-span-full"
                >
                  <Textarea
                    placeholder="Ceritakan tentang diri Anda..."
                    rows={4}
                    {...field}
                  />
                </FormFieldset>
              )}
            />

            {/* Duration Stay in Japan */}
            <FormField
              control={form.control}
              name="possible_duration_to_stay_in_japan"
              render={({ field }) => (
                <FormFieldset
                  label="Durasi Tinggal di Jepang"
                  className="col-span-full md:col-span-3"
                >
                  <FormFieldSelect
                    placeholder="Pilih durasi"
                    items={DURATION_STAY_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                </FormFieldset>
              )}
            />
          </div>
          <CardProfile.Footer>
            <CardProfile.SubmissionButton />
          </CardProfile.Footer>
        </form>
      </CardProfile>
    </Form>
  )
}

type PersonalInformationData =
  | GetUserPersonalInfoQuery['getUserPersonalInfo']['data']
  | null

function getDefaultValues(initial?: PersonalInformationData): FormDto {
  return {
    name: valueWithFallback(initial?.name, ''),
    name_hiragana: valueWithFallback(initial?.name_hiragana, ''),
    address: valueWithFallback(initial?.address, ''),
    photo_profile: valueWithFallback(initial?.photo, ''),
    birthdate: valueDateWithFallback(initial?.birthdate),
    // Handle enum types with proper casting and fallbacks
    gender: initial?.gender || Gender.Men,
    marital_status: initial?.marital_status || undefined,
    age: valueWithFallback(initial?.age, 0),
    spouse_responsibility:
      (initial?.spouse_responsibility as unknown as 'yes' | 'no') || 'no',
    spouse_count: valueWithFallback(initial?.spouse_count, 0),
    jiko_pr: valueWithFallback(initial?.jiko_pr, ''),
    possible_duration_to_stay_in_japan:
      (initial?.possible_duration_to_stay_in_japan as unknown as
        | '3_tahun'
        | '3_5_tahun'
        | 'lebih_dari_5_tahun'
        | 'tergantung_situasi') || undefined,
  }
}

interface FormDto extends z.infer<typeof FormDto> {}
const FormDto = z.object({
  name: z.string().min(1, 'Required'),
  name_hiragana: z.string().optional(),
  address: z
    .string()
    .min(1, 'Address is required')
    .max(100, 'Address is too long'),
  photo_profile: z.union([z.instanceof(File), z.string()]).optional(),
  birthdate: z.coerce.date({ message: 'Required' }),
  // Backend enum values: 'men', 'women'
  gender: z.nativeEnum(Gender, { message: 'Required' }),
  age: z.number().min(1, 'Age must be at least 1').max(150, 'Age too high'),
  // Backend enum MaritalStatus - will be validated by GraphQL
  marital_status: z.string().optional(),
  // Backend enum values: 'yes', 'no'
  spouse_responsibility: z.enum(['yes', 'no']).optional(),
  // Backend field name is spouse_count (Int)
  spouse_count: z.number().min(0, 'Cannot be negative').default(0),
  jiko_pr: z.string().optional(),
  // Backend enum values: '3_tahun','3_5_tahun','lebih_dari_5_tahun','tergantung_situasi'
  possible_duration_to_stay_in_japan: z
    .enum(['3_tahun', '3_5_tahun', 'lebih_dari_5_tahun', 'tergantung_situasi'])
    .optional(),
})

function constructPayload(
  data: FormDto,
  meta?: { id: number; created_at: string }
) {
  const formData = new FormData()

  // Update
  if (meta) {
    formData.set('id', String(meta.id))
    formData.set('created_at', formatDateTimeServer(new Date(meta.created_at)))
  }

  if (data.photo_profile) {
    typeof data.photo_profile === 'string'
      ? formData.set('photo', data.photo_profile)
      : formData.set('photo_profile', data.photo_profile)
  }

  if (data.name_hiragana) formData.set('name_hiragana', data.name_hiragana)
  // Required fields
  formData.set('name', data.name)
  formData.set('address', data.address)
  formData.set('birth_date', formatDateOnly(data.birthdate))
  formData.set('gender', data.gender)
  formData.set('age', String(data.age))

  // Optional fields
  if (data.marital_status) {
    formData.set('marital_status', data.marital_status)
  }

  if (data.spouse_responsibility) {
    formData.set('spouse_responsibility', data.spouse_responsibility)
  }

  formData.set('spouse_count', String(data.spouse_count))

  if (data.jiko_pr) {
    formData.set('jiko_pr', data.jiko_pr)
  }

  if (data.possible_duration_to_stay_in_japan) {
    formData.set(
      'possible_duration_to_stay_in_japan',
      data.possible_duration_to_stay_in_japan
    )
  }

  return formData
}
